import { ArrowBack } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { Button } from './ui/Button';

const { semantic } = colorTokens;

/**
 * Shared App / PageHeader composite.
 * Supports List/Detail layout: title, subtitle, badge, actions.
 * Preserves legacy backTo for existing detail pages.
 * Do not pass breadcrumb when AppShell topbar already provides it.
 */
export function PageHeader({
  title,
  subtitle,
  breadcrumb,
  badge,
  actions,
  backTo,
  type = 'List',
}) {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const isDetail = type === 'Detail';

  return (
    <Box
      sx={{
        mb: isDetail ? 2.5 : 3,
        pb: isDetail ? 2 : 2.5,
        borderBottom: `1px solid ${semantic.border.subtle}`,
      }}
      data-page-header-type={type}
    >
      {backTo && (
        <Button
          variant="Ghost"
          onClick={() => navigate(backTo)}
          sx={{ mb: 1, minHeight: 32, px: 1 }}
        >
          <ArrowBack fontSize="small" sx={{ mr: 0.75 }} />
          {t('back')}
        </Button>
      )}

      {breadcrumb && (
        <Typography
          sx={{
            mb: 1,
            fontSize: 12,
            letterSpacing: '0.24px',
            color: semantic.text.muted,
          }}
        >
          {breadcrumb}
        </Typography>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography
              component="h1"
              sx={{
                fontFamily: typographyTokens.fontFamilyDisplay,
                fontWeight: typographyTokens.fontWeightBold,
                fontSize: { xs: 24, md: 32 },
                lineHeight: { xs: '32px', md: '40px' },
                letterSpacing: '-0.64px',
                color: semantic.text.primary,
              }}
            >
              {title}
            </Typography>
            {badge}
          </Stack>
          {subtitle && (
            <Typography
              sx={{
                mt: 0.5,
                fontSize: 13,
                lineHeight: '18px',
                color: semantic.text.secondary,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {actions && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {actions}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
